import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import Select2Dropdown from '../common/Select2Dropdown';
import { usePermissions } from '../../services/PermissionsContext';
import useSortableTable from '../common/useSortableTable';
import { openFilePreview } from '../common/FilePreviewModal';

const API_RECEIVABLES = '/api/admin/receivables/';
const API_OPTIONS = '/api/admin/receivables/get_options/';
const API_CUSTOMERS = '/api/masters/customers/';

const MODULE = 'RECEIVABLES';

const CASH_CURRENCIES = [
    { id: 'TZS', text: 'TZS' },
    { id: 'USD', text: 'USD' },
];

const MODES = [
    { id: 'CASH', text: 'CASH' },
    { id: 'CHEQUE', text: 'CHEQUE' },
    { id: 'BANK TRANSFER', text: 'BANK TRANSFER' },
    { id: 'BANK DEPOSIT', text: 'BANK DEPOSIT' },
];


const authHeaders = (json = true) => {
    const token = localStorage.getItem('kk_token');
    return {
        ...(json ? { 'Content-Type': 'application/json' } : {}),
        'Authorization': `Bearer ${token}`,
    };
};

const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount || 0);

const formatAmountInput = (raw) => {
    if (!raw && raw !== 0) return '';
    const str = String(raw).replace(/[^0-9.]/g, '');
    const parts = str.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return parts.length > 1 ? `${parts[0]}.${parts[1]}` : parts[0];
};

const formatDateDisplay = (value) => {
    if (!value) return '-';
    const str = String(value);
    const match = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) return `${match[3]}/${match[2]}/${match[1]}`;
    return str;
};

const formatDateTime = (value) => {
    if (!value) return '-';
    const d = new Date(value);
    if (isNaN(d)) return '-';
    const pad = n => n.toString().padStart(2, '0');
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const todayISO = () => new Date().toISOString().split('T')[0];

const addDays = (dateStr, days) => {
    const [y, m, d] = dateStr.split('-').map(Number);
    const date = new Date(y, m - 1, d);
    date.setDate(date.getDate() + days);
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
};

const FILTER_MODES = [
    { id: 'daily', label: 'Daily' },
    { id: 'minus1d', label: '-1D' },
    { id: 'minus1w', label: '-1W' },
    { id: 'minus1m', label: '-1M' },
    { id: 'minus3m', label: '-3M' },
    { id: 'range', label: 'Date Range' },
];

const emptyForm = () => ({
    customer: '',
    mode: '',
    is_refund: false,
    bank_account: '',
    currency: 'TZS',
    date: '',
    amount: '',
    reference_no: '',
    brought_by: '',
    remark: '',
    document_file: null,
});

const Receivables = () => {
    const { hasPermission } = usePermissions();
    const canCreate = hasPermission(MODULE, 'can_create');
    const canEdit = hasPermission(MODULE, 'can_edit');
    const canDelete = hasPermission(MODULE, 'can_delete');

    // Options
    const [customers, setCustomers] = useState([]);
    const [bankOptions, setBankOptions] = useState([]);

    // Filter state
    const [filterMode, setFilterMode] = useState('daily'); // 'daily' | 'range'
    const [filterCustomer, setFilterCustomer] = useState('');
    const [filterBankAccount, setFilterBankAccount] = useState('');
    const [filterRefund, setFilterRefund] = useState('');
    const [filterDateFrom, setFilterDateFrom] = useState(todayISO());
    const [filterDateTo, setFilterDateTo] = useState(todayISO());

    // List state
    const [receivables, setReceivables] = useState([]);
    const [loading, setLoading] = useState(false);
    const [allReferenceNos, setAllReferenceNos] = useState([]);

    // Modal state
    const [showModal, setShowModal] = useState(false);
    const [editingReceivable, setEditingReceivable] = useState(null);
    const [formData, setFormData] = useState(emptyForm());
    const [formErrors, setFormErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [replaceDocument, setReplaceDocument] = useState(false);

    // Delete confirm
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);

    // Sorting
    const { sortConfig, requestSort, getSortIndicator, items: sortedItems } = useSortableTable(receivables, { key: 'date', direction: 'descending' });

    // Fetch options on mount
    useEffect(() => {
        const fetchOptions = async () => {
            try {
                const res = await fetch(API_OPTIONS, { headers: authHeaders(false) });
                if (!res.ok) throw new Error();
                const data = await res.json();
                setCustomers((data.customers || []).map(c => ({ id: c.id, text: (c.name || '').toUpperCase() })));
                setBankOptions((data.banks || []).map(b => ({ ...b, text: (b.text || '').toUpperCase() })));
            } catch {
                toast.error('Failed to load options');
            }
        };
        fetchOptions();
    }, []);

    // Fetch all reference numbers (unfiltered) for accurate next CASH ref calculation
    const fetchAllReferenceNumbers = useCallback(async () => {
        try {
            const params = new URLSearchParams();
            params.set('date_from', '1900-01-01');
            params.set('date_to', '2099-12-31');
            const res = await fetch(`${API_RECEIVABLES}?${params.toString()}`, { headers: authHeaders(false) });
            if (!res.ok) throw new Error();
            const data = await res.json();
            const items = Array.isArray(data) ? data : (data.results || []);
            setAllReferenceNos(items.map(r => r.reference_no || ''));
        } catch {
            // silent: reference counter falls back to already-known numbers
        }
    }, []);

    useEffect(() => {
        fetchAllReferenceNumbers();
    }, [fetchAllReferenceNumbers]);

    // Fetch receivables
    const fetchReceivables = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (filterCustomer) params.set('customer', filterCustomer);
            if (filterBankAccount) params.set('bank_account', filterBankAccount);
            if (filterRefund !== '') params.set('is_refund', filterRefund);
            if (filterMode === 'daily') {
                params.set('daily', '1');
            } else if (filterMode === 'range') {
                if (filterDateFrom) params.set('date_from', filterDateFrom);
                if (filterDateTo) params.set('date_to', filterDateTo);
            } else {
                let dateFrom = filterDateFrom;
                let dateTo = filterDateTo;
                if (filterMode === 'minus1d') { dateFrom = addDays(dateTo, -1); }
                else if (filterMode === 'minus1w') { dateFrom = addDays(dateTo, -6); }
                else if (filterMode === 'minus1m') { dateFrom = addDays(dateTo, -29); }
                else if (filterMode === 'minus3m') { dateFrom = addDays(dateTo, -89); }
                if (dateFrom) params.set('date_from', dateFrom);
                if (dateTo) params.set('date_to', dateTo);
            }
            const res = await fetch(`${API_RECEIVABLES}?${params.toString()}`, { headers: authHeaders(false) });
            if (!res.ok) throw new Error();
            const data = await res.json();
            setReceivables(Array.isArray(data) ? data : (data.results || []));
        } catch {
            toast.error('Failed to load receivables');
        } finally {
            setLoading(false);
        }
    }, [filterMode, filterCustomer, filterBankAccount, filterRefund, filterDateFrom, filterDateTo]);

    // Sync date range when a preset mode is selected
    useEffect(() => {
        const today = todayISO();
        if (filterMode === 'minus1d') {
            setFilterDateFrom(addDays(today, -1));
            setFilterDateTo(today);
        } else if (filterMode === 'minus1w') {
            setFilterDateFrom(addDays(today, -6));
            setFilterDateTo(today);
        } else if (filterMode === 'minus1m') {
            setFilterDateFrom(addDays(today, -29));
            setFilterDateTo(today);
        } else if (filterMode === 'minus3m') {
            setFilterDateFrom(addDays(today, -89));
            setFilterDateTo(today);
        }
    }, [filterMode]);

    useEffect(() => {
        fetchReceivables();
    }, [fetchReceivables]);

    const handleClearFilters = () => {
        setFilterMode('daily');
        setFilterCustomer('');
        setFilterBankAccount('');
        setFilterRefund('');
        setFilterDateFrom(todayISO());
        setFilterDateTo(todayISO());
    };

    // Next CASH reference numbers (independent counters for receipts and refunds).
    // Legacy CASH-XXXX numbers seed the starting point so new prefixes never collide.
    const nextCashRefNo = useMemo(() => {
        const allRefs = allReferenceNos.length > 0 ? allReferenceNos : receivables.map(r => r.reference_no || '');
        const legacyRefs = allRefs
            .filter(ref => /^CASH-\d+$/.test(ref))
            .map(ref => parseInt(ref.replace('CASH-', ''), 10));
        const legacyMax = legacyRefs.length > 0 ? Math.max(...legacyRefs) : 0;
        const recRefs = allRefs
            .filter(ref => /^CASH-REC-\d+$/.test(ref))
            .map(ref => parseInt(ref.replace('CASH-REC-', ''), 10));
        const recMax = recRefs.length > 0 ? Math.max(...recRefs) : legacyMax;
        const refRefs = allRefs
            .filter(ref => /^CASH-REF-\d+$/.test(ref))
            .map(ref => parseInt(ref.replace('CASH-REF-', ''), 10));
        const refMax = refRefs.length > 0 ? Math.max(...refRefs) : legacyMax;
        return {
            rec: `CASH-REC-${String(recMax + 1).padStart(4, '0')}`,
            ref: `CASH-REF-${String(refMax + 1).padStart(4, '0')}`,
        };
    }, [receivables]);

    // Totals by currency (all payments); refunds are deducted
    const currencyTotals = useMemo(() => {
        const map = {};
        receivables.forEach(r => {
            const cur = r.currency || 'TZS';
            if (!map[cur]) map[cur] = { received: 0, refund: 0, net: 0 };
            const amount = parseFloat(r.amount || 0);
            if (r.is_refund) {
                map[cur].refund += amount;
                map[cur].net -= amount;
            } else {
                map[cur].received += amount;
                map[cur].net += amount;
            }
        });
        return map;
    }, [receivables]);

    // Totals by currency for CASH payments only; refunds are deducted
    const cashTotals = useMemo(() => {
        const map = {};
        receivables.filter(r => r.mode === 'CASH').forEach(r => {
            const cur = r.currency || 'TZS';
            if (!map[cur]) map[cur] = { received: 0, refund: 0, net: 0 };
            const amount = parseFloat(r.amount || 0);
            if (r.is_refund) {
                map[cur].refund += amount;
                map[cur].net -= amount;
            } else {
                map[cur].received += amount;
                map[cur].net += amount;
            }
        });
        return map;
    }, [receivables]);

    // Totals by currency for non-CASH (bank) payments; refunds are deducted
    const bankTotalsByCurrency = useMemo(() => {
        const map = {};
        receivables.filter(r => r.mode !== 'CASH').forEach(r => {
            const cur = r.currency || 'TZS';
            if (!map[cur]) map[cur] = { received: 0, refund: 0, net: 0 };
            const amount = parseFloat(r.amount || 0);
            if (r.is_refund) {
                map[cur].refund += amount;
                map[cur].net -= amount;
            } else {
                map[cur].received += amount;
                map[cur].net += amount;
            }
        });
        return map;
    }, [receivables]);

    // Per-bank-account totals; refunds are deducted
    const bankTotals = useMemo(() => {
        const map = {};
        receivables.forEach(r => {
            if (r.bank_account) {
                const key = r.bank_account_name || String(r.bank_account);
                if (!map[key]) map[key] = { amount: 0, currency: r.currency || 'TZS' };
                const amount = parseFloat(r.amount || 0);
                map[key].amount += r.is_refund ? -amount : amount;
            }
        });
        return map;
    }, [receivables]);

    // Form helpers
    const isCash = formData.mode === 'CASH';

    // Prefer previewing the physical file directly; only fall back to a blob
    // copy when the file cannot be displayed in the preview iframe (unreachable,
    // or blocked by X-Frame-Options — e.g. Django dev server sends DENY).
    const openRemoteFilePreview = async (fileUrl, title) => {
        // The preview detects file type from the URL/filename extension —
        // blob URLs have none, so always pass the real file name (or .pdf).
        const fileName = (fileUrl.split('/').pop().split('?')[0]) || `${title}.pdf`;
        const canEmbed = async () => {
            try {
                const head = await fetch(fileUrl, { method: 'HEAD' });
                if (!head.ok && head.status !== 405) return false;
                const xfo = (head.headers.get('x-frame-options') || '').toUpperCase();
                if (xfo === 'DENY') return false;
                if (xfo === 'SAMEORIGIN') {
                    try { return new URL(fileUrl, window.location.href).origin === window.location.origin; }
                    catch { return false; }
                }
                return true;
            } catch { return false; }
        };
        try {
            if (await canEmbed()) {
                openFilePreview(fileUrl, fileName);
                return;
            }
            const res = await fetch(fileUrl);
            if (!res.ok) throw new Error();
            const blobUrl = URL.createObjectURL(await res.blob());
            openFilePreview(blobUrl, fileName);
        } catch {
            toast.error('Failed to load file preview');
        }
    };
    const uppercaseFields = ['reference_no', 'remark', 'brought_by'];
    const handleFormChange = (field, value) => {
        const normalized = uppercaseFields.includes(field) && typeof value === 'string'
            ? value.toUpperCase()
            : value;
        if (field === 'bank_account') {
            const bank = bankOptions.find(b => String(b.id) === String(value));
            setFormData(prev => ({
                ...prev,
                bank_account: normalized,
                currency: bank ? bank.currency : prev.currency,
            }));
        } else if (field === 'mode') {
            const isSwitchingToCash = normalized === 'CASH';
            const cashRef = formData.is_refund ? nextCashRefNo.ref : nextCashRefNo.rec;
            setFormData(prev => ({
                ...prev,
                mode: normalized,
                bank_account: '',
                currency: isSwitchingToCash ? 'TZS' : prev.currency,
                reference_no: isSwitchingToCash ? cashRef : '',
            }));
        } else if (field === 'is_refund') {
            const cashRef = normalized ? nextCashRefNo.ref : nextCashRefNo.rec;
            setFormData(prev => ({
                ...prev,
                is_refund: normalized,
                reference_no: prev.mode === 'CASH' ? cashRef : prev.reference_no,
            }));
        } else {
            setFormData(prev => ({ ...prev, [field]: normalized }));
        }
        if (formErrors[field]) setFormErrors(prev => ({ ...prev, [field]: '' }));
    };

    const validateForm = () => {
        const errs = {};
        if (!formData.customer) errs.customer = 'Customer is required';
        if (!formData.mode) errs.mode = 'Mode is required';
        if (formData.mode !== 'CASH' && !formData.bank_account) errs.bank_account = 'Bank is required';
        if (formData.mode === 'CASH' && !formData.currency) errs.currency = 'Currency is required';
        if (!formData.date) errs.date = 'Date is required';
        if (!formData.amount || parseFloat(formData.amount) <= 0) errs.amount = 'Amount is required';
        if (formData.mode !== 'CASH' && !formData.reference_no.trim()) errs.reference_no = 'Reference No is required';
        if (formData.mode === 'CASH' && !formData.brought_by.trim()) errs.brought_by = 'Brought By is required';
        return errs;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const errs = validateForm();
        if (Object.keys(errs).length > 0) {
            setFormErrors(errs);
            return;
        }
        setSubmitting(true);
        try {
            const payload = new FormData();
            payload.append('customer', formData.customer);
            payload.append('mode', formData.mode);
            payload.append('is_refund', formData.is_refund ? 'true' : 'false');
            if (formData.bank_account) payload.append('bank_account', formData.bank_account);
            payload.append('currency', formData.currency);
            payload.append('date', formData.date);
            payload.append('amount', formData.amount);
            payload.append('reference_no', (formData.reference_no || '').toUpperCase());
            payload.append('brought_by', (formData.brought_by || '').toUpperCase());
            payload.append('remark', (formData.remark || '').toUpperCase());
            payload.append('document_type', 'PROOF OF RECEIVABLE');
            if (formData.document_file) {
                payload.append('document_file', formData.document_file);
            }
            const isEdit = !!editingReceivable;
            const url = isEdit ? `${API_RECEIVABLES}${editingReceivable.id}/` : API_RECEIVABLES;
            const res = await fetch(url, {
                method: isEdit ? 'PATCH' : 'POST',
                headers: { 'Authorization': `Bearer ${localStorage.getItem('kk_token')}` },
                body: payload,
            });
            if (!res.ok) {
                const errData = await res.json().catch(() => ({}));
                const msg = Object.values(errData).flat().join(' ') || 'Failed to save receivable';
                toast.error(msg);
                return;
            }
            const savedData = await res.json().catch(() => ({}));
            toast.success(editingReceivable ? 'Receivable updated successfully' : 'Receivable saved successfully');
            if (savedData.acknowledgement_file_url) {
                openRemoteFilePreview(savedData.acknowledgement_file_url, 'Received Acknowledgement');
            }
            setShowModal(false);
            setEditingReceivable(null);
            setFormData(emptyForm());
            setFormErrors({});
            const savedDate = savedData.date;
            if (savedDate && filterMode === 'daily' && savedDate !== todayISO()) {
                setFilterMode('range');
                setFilterDateFrom(savedDate);
                setFilterDateTo(savedDate);
            } else {
                fetchReceivables();
            }
            fetchAllReferenceNumbers();
        } catch {
            toast.error('Failed to save receivable');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            const res = await fetch(`${API_RECEIVABLES}${deleteTarget.id}/`, {
                method: 'DELETE',
                headers: authHeaders(false),
            });
            if (!res.ok) throw new Error();
            toast.success('Receivable deleted');
            setDeleteTarget(null);
            fetchReceivables();
            fetchAllReferenceNumbers();
        } catch {
            toast.error('Failed to delete receivable');
        } finally {
            setDeleting(false);
        }
    };

    const openModal = () => {
        setEditingReceivable(null);
        setReplaceDocument(false);
        setFormData(emptyForm());
        setFormErrors({});
        setShowModal(true);
    };

    const openEditModal = (r) => {
        setEditingReceivable(r);
        setReplaceDocument(false);
        setFormData({
            customer: r.customer ? Number(r.customer) : '',
            mode: r.mode || '',
            is_refund: r.is_refund || false,
            bank_account: r.bank_account ? Number(r.bank_account) : '',
            currency: r.currency || 'TZS',
            date: r.date || '',
            amount: String(r.amount || ''),
            reference_no: r.reference_no || '',
            brought_by: r.brought_by || '',
            remark: r.remark || '',
            document_file: null,
        });
        setFormErrors({});
        setShowModal(true);
    };

    // Resolve customer name for display
    const getCustomerName = (id) => {
        const c = customers.find(c => String(c.id) === String(id));
        return c ? c.text : id;
    };

    const getBankName = (id) => {
        const b = bankOptions.find(b => String(b.id) === String(id));
        return b ? b.text : id;
    };

    return (
        <div style={{ padding: '16px 20px' }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
                <h2 style={{ margin: 0 }}>Receivables</h2>
                {canCreate && (
                    <button
                        onClick={openModal}
                        style={{
                            background: '#1a73e8', color: '#fff', border: 'none',
                            borderRadius: 6, padding: '8px 18px', fontWeight: 600,
                            cursor: 'pointer', fontSize: '0.95rem'
                        }}
                    >
                        + New Receivable
                    </button>
                )}
            </div>

            {/* Filter Bar */}
            <div style={{
                background: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: 8,
                padding: '12px 16px', marginBottom: 16,
                display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end'
            }}>
                {/* Mode Toggle */}
                <div>
                    <label style={{ fontSize: 11, color: '#666', display: 'block', marginBottom: 3 }}>VIEW MODE</label>
                    <div style={{ display: 'flex', borderRadius: 6, overflow: 'hidden', border: '1px solid #ced4da' }}>
                        {FILTER_MODES.map(mode => (
                            <button
                                key={mode.id}
                                onClick={() => setFilterMode(mode.id)}
                                style={{
                                    padding: '6px 14px', border: 'none', cursor: 'pointer', fontSize: '0.85rem',
                                    background: filterMode === mode.id ? '#1a73e8' : '#fff',
                                    color: filterMode === mode.id ? '#fff' : '#333',
                                    fontWeight: filterMode === mode.id ? 600 : 400,
                                }}
                            >
                                {mode.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Customer Filter */}
                <div style={{ minWidth: 220 }}>
                    <label style={{ fontSize: 11, color: '#666', display: 'block', marginBottom: 3 }}>CUSTOMER</label>
                    <Select2Dropdown
                        options={[{ id: '', text: 'All Customers' }, ...customers]}
                        value={filterCustomer}
                        onChange={val => setFilterCustomer(val)}
                        placeholder="All Customers"
                        displayKey="text"
                        valueKey="id"
                    />
                </div>
                <div style={{ minWidth: 220 }}>
                    <label style={{ fontSize: 11, color: '#666', display: 'block', marginBottom: 3 }}>BANK ACCOUNT</label>
                    <Select2Dropdown
                        options={[{ id: '', text: 'All Bank Accounts' }, ...bankOptions]}
                        value={filterBankAccount}
                        onChange={val => setFilterBankAccount(val)}
                        placeholder="All Bank Accounts"
                        displayKey="text"
                        valueKey="id"
                    />
                </div>
                <div style={{ minWidth: 150 }}>
                    <label style={{ fontSize: 11, color: '#666', display: 'block', marginBottom: 3 }}>REFUND STATUS</label>
                    <Select2Dropdown
                        options={[
                            { id: '', text: 'All' },
                            { id: 'true', text: 'Refund' },
                            { id: 'false', text: 'RECEIVABLE' },
                        ]}
                        value={filterRefund}
                        onChange={val => setFilterRefund(val)}
                        placeholder="All"
                        displayKey="text"
                        valueKey="id"
                    />
                </div>

                {/* Date Range (shown for presets and custom range) */}
                {filterMode !== 'daily' && (
                    <>
                        <div>
                            <label style={{ fontSize: 11, color: '#666', display: 'block', marginBottom: 3 }}>FROM DATE</label>
                            <input
                                type="date" value={filterDateFrom}
                                onChange={e => setFilterDateFrom(e.target.value)}
                                readOnly={filterMode !== 'range'}
                                style={{ padding: '6px 10px', borderRadius: 5, border: '1px solid #ced4da', fontSize: '0.9rem', backgroundColor: filterMode !== 'range' ? '#e9ecef' : '#fff' }}
                            />
                        </div>
                        <div>
                            <label style={{ fontSize: 11, color: '#666', display: 'block', marginBottom: 3 }}>TO DATE</label>
                            <input
                                type="date" value={filterDateTo}
                                onChange={e => setFilterDateTo(e.target.value)}
                                readOnly={filterMode !== 'range'}
                                style={{ padding: '6px 10px', borderRadius: 5, border: '1px solid #ced4da', fontSize: '0.9rem', backgroundColor: filterMode !== 'range' ? '#e9ecef' : '#fff' }}
                            />
                        </div>
                    </>
                )}

                <button
                    onClick={handleClearFilters}
                    style={{
                        background: '#6c757d', color: '#fff', border: 'none',
                        borderRadius: 6, padding: '7px 16px', fontWeight: 600, cursor: 'pointer', fontSize: '0.9rem'
                    }}
                >
                    Clear
                </button>
            </div>

            {/* Summary */}
            <div style={{ display: 'flex', gap: 12, marginBottom: 12, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                {/* Date + Count */}
                <div style={{ display: 'flex', flexDirection: 'row', gap: 8 }}>
                    <div style={{ background: '#fff3e0', border: '1px solid #ffcc80', borderRadius: 8, padding: '10px 20px', minWidth: 140 }}>
                        <div style={{ fontSize: 11, color: '#e65100', fontWeight: 600 }}>DATE</div>
                        {filterMode === 'daily' ? (
                            <div style={{ fontSize: '0.95rem', fontWeight: 700, color: '#bf360c' }}>{formatDateDisplay(todayISO())}</div>
                        ) : (
                            <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#bf360c' }}>
                                {filterDateFrom ? formatDateDisplay(filterDateFrom) : '—'}
                                <span style={{ margin: '0 4px' }}>→</span>
                                {filterDateTo ? formatDateDisplay(filterDateTo) : '—'}
                            </div>
                        )}
                    </div>
                    <div style={{ background: '#e3f2fd', border: '1px solid #90caf9', borderRadius: 8, padding: '10px 20px', minWidth: 100 }}>
                        <div style={{ fontSize: 11, color: '#1565c0', fontWeight: 600 }}>COUNT</div>
                        <div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#0d47a1' }}>{receivables.length}</div>
                    </div>
                </div>
                {/* Totals by currency */}
                {Object.keys(currencyTotals).length > 0 && (
                    <div style={{ background: '#e8f5e9', border: '1px solid #a5d6a7', borderRadius: 8, padding: '10px 16px', minWidth: 240 }}>
                        <div style={{ fontSize: 11, color: '#388e3c', fontWeight: 700, marginBottom: 6 }}>TOTALS BY CURRENCY</div>
                        {Object.entries(currencyTotals).map(([cur, data]) => (
                            <div key={cur} style={{ borderBottom: '1px solid #c8e6c9', padding: '3px 0', marginBottom: 4 }}>
                                <div style={{ fontWeight: 700, color: '#2e7d32', fontSize: '0.85rem' }}>{cur}</div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, fontSize: '0.8rem' }}>
                                    <span style={{ color: '#388e3c' }}>Total Received:</span>
                                    <span style={{ fontWeight: 700, color: '#1b5e20' }}>{formatCurrency(data.received)}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, fontSize: '0.8rem' }}>
                                    <span style={{ color: '#e65100' }}>Total Refund:</span>
                                    <span style={{ fontWeight: 700, color: '#e65100' }}>{formatCurrency(data.refund)}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, fontSize: '0.85rem', borderTop: '1px dashed #a5d6a7', marginTop: 2, paddingTop: 2 }}>
                                    <span style={{ fontWeight: 700, color: '#1b5e20' }}>Net Total:</span>
                                    <span style={{ fontWeight: 700, color: '#1b5e20' }}>{formatCurrency(data.net)}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
                {/* Totals by currency for Cash payments */}
                {Object.keys(cashTotals).length > 0 && (
                    <div style={{ background: '#fff8e1', border: '1px solid #ffe082', borderRadius: 8, padding: '10px 16px', minWidth: 240 }}>
                        <div style={{ fontSize: 11, color: '#f57f17', fontWeight: 700, marginBottom: 6 }}>TOTALS BY CASH</div>
                        {Object.entries(cashTotals).map(([cur, data]) => (
                            <div key={cur} style={{ borderBottom: '1px solid #ffecb3', padding: '3px 0', marginBottom: 4 }}>
                                <div style={{ fontWeight: 700, color: '#e65100', fontSize: '0.85rem' }}>{cur}</div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, fontSize: '0.8rem' }}>
                                    <span style={{ color: '#e65100' }}>Total Received:</span>
                                    <span style={{ fontWeight: 700, color: '#1b5e20' }}>{formatCurrency(data.received)}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, fontSize: '0.8rem' }}>
                                    <span style={{ color: '#e65100' }}>Total Refund:</span>
                                    <span style={{ fontWeight: 700, color: '#e65100' }}>{formatCurrency(data.refund)}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, fontSize: '0.85rem', borderTop: '1px dashed #ffe082', marginTop: 2, paddingTop: 2 }}>
                                    <span style={{ fontWeight: 700, color: '#bf360c' }}>Net Total:</span>
                                    <span style={{ fontWeight: 700, color: '#bf360c' }}>{formatCurrency(data.net)}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
                {/* Totals by currency for Bank payments */}
                {Object.keys(bankTotalsByCurrency).length > 0 && (
                    <div style={{ background: '#e3f2fd', border: '1px solid #90caf9', borderRadius: 8, padding: '10px 16px', minWidth: 240 }}>
                        <div style={{ fontSize: 11, color: '#1565c0', fontWeight: 700, marginBottom: 6 }}>TOTALS BY BANK</div>
                        {Object.entries(bankTotalsByCurrency).map(([cur, data]) => (
                            <div key={cur} style={{ borderBottom: '1px solid #bbdefb', padding: '3px 0', marginBottom: 4 }}>
                                <div style={{ fontWeight: 700, color: '#0d47a1', fontSize: '0.85rem' }}>{cur}</div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, fontSize: '0.8rem' }}>
                                    <span style={{ color: '#1565c0' }}>Total Received:</span>
                                    <span style={{ fontWeight: 700, color: '#1b5e20' }}>{formatCurrency(data.received)}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, fontSize: '0.8rem' }}>
                                    <span style={{ color: '#e65100' }}>Total Refund:</span>
                                    <span style={{ fontWeight: 700, color: '#e65100' }}>{formatCurrency(data.refund)}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, fontSize: '0.85rem', borderTop: '1px dashed #90caf9', marginTop: 2, paddingTop: 2 }}>
                                    <span style={{ fontWeight: 700, color: '#0d47a1' }}>Net Total:</span>
                                    <span style={{ fontWeight: 700, color: '#0d47a1' }}>{formatCurrency(data.net)}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
                {/* Per bank account totals */}
                {Object.keys(bankTotals).length > 0 && (
                    <div style={{ background: '#f3e5f5', border: '1px solid #ce93d8', borderRadius: 8, padding: '10px 16px', minWidth: 220 }}>
                        <div style={{ fontSize: 11, color: '#6a1b9a', fontWeight: 700, marginBottom: 6 }}>TOTALS BY BANK ACCOUNT</div>
                        {Object.entries(bankTotals).map(([bankName, data]) => (
                            <div key={bankName} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: '0.85rem', borderBottom: '1px solid #e1bee7', padding: '3px 0' }}>
                                <span style={{ fontWeight: 700, color: '#6a1b9a', textTransform: 'uppercase' }}>{bankName}</span>
                                <span style={{ fontWeight: 700, color: '#4a148c' }}>{data.currency} {formatCurrency(data.amount)}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Table */}
            <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                    <thead>
                        <tr style={{ background: '#343a40', color: '#fff' }}>
                            <th style={thStyle} onClick={() => requestSort('date')} className="sortable">
                                Date {getSortIndicator('date')}
                            </th>
                            <th style={thStyle} onClick={() => requestSort('customer_name')} className="sortable">
                                Customer {getSortIndicator('customer_name')}
                            </th>
                            <th style={thStyle} onClick={() => requestSort('mode')} className="sortable">
                                Mode {getSortIndicator('mode')}
                            </th>
                            <th style={thStyle}>Status</th>
                            <th style={thStyle}>Bank Account</th>
                            <th style={{ ...thStyle, textAlign: 'right' }} onClick={() => requestSort('amount')} className="sortable">
                                Amount {getSortIndicator('amount')}
                            </th>
                            <th style={thStyle} onClick={() => requestSort('currency')} className="sortable">
                                Currency {getSortIndicator('currency')}
                            </th>
                            <th style={thStyle} onClick={() => requestSort('reference_no')} className="sortable">
                                Reference No {getSortIndicator('reference_no')}
                            </th>
                            <th style={thStyle}>Remark</th>
                            <th style={thStyle}>Document</th>
                            <th style={thStyle} onClick={() => requestSort('created_by_name')} className="sortable">
                                Created By {getSortIndicator('created_by_name')}
                            </th>
                            {canDelete && <th style={thStyle}>Action</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan={canDelete ? 10 : 9} style={{ textAlign: 'center', padding: 32, color: '#888' }}>
                                    Loading...
                                </td>
                            </tr>
                        ) : sortedItems.length === 0 ? (
                            <tr>
                                <td colSpan={canDelete ? 10 : 9} style={{ textAlign: 'center', padding: 32, color: '#888' }}>
                                    No receivables found
                                </td>
                            </tr>
                        ) : (
                            sortedItems.map((r, idx) => (
                                <tr
                                    key={r.id}
                                    style={{ background: idx % 2 === 0 ? '#fff' : '#f8f9fa', borderBottom: '1px solid #dee2e6' }}
                                >
                                    <td style={tdStyle}>{formatDateDisplay(r.date)}</td>
                                    <td style={{ ...tdStyle, textTransform: 'uppercase' }}>{r.customer_name || '-'}</td>
                                    <td style={tdStyle}>
                                        <span style={{
                                            background: paymentModeColor(r.mode).bg,
                                            color: paymentModeColor(r.mode).color,
                                            borderRadius: 4, padding: '2px 8px', fontSize: '0.8rem', fontWeight: 600
                                        }}>
                                            {r.mode}
                                        </span>
                                    </td>
                                    <td style={tdStyle}>
                                        {r.is_refund ? (
                                            <span style={{ background: '#fff3e0', color: '#e65100', borderRadius: 4, padding: '2px 8px', fontSize: '0.8rem', fontWeight: 600 }}>REFUND</span>
                                        ) : (
                                            <span style={{ background: '#e8f5e9', color: '#2e7d32', borderRadius: 4, padding: '2px 8px', fontSize: '0.8rem', fontWeight: 600 }}>RECEIVABLE</span>
                                        )}
                                    </td>
                                    <td style={{ ...tdStyle, textTransform: 'uppercase' }}>{r.bank_account_name || '-'}</td>
                                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                                        {formatCurrency(r.amount)}
                                    </td>
                                    <td style={{ ...tdStyle, fontWeight: 600, color: '#5c35a0' }}>{r.currency || 'TZS'}</td>
                                    <td style={{ ...tdStyle, textTransform: 'uppercase' }}>{r.reference_no || '-'}</td>
                                    <td style={{ ...tdStyle, maxWidth: 300, whiteSpace: 'normal', wordWrap: 'break-word', textTransform: 'uppercase' }}>
                                        {r.remark || '-'}
                                    </td>
                                    <td style={tdStyle}>
                                        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                                            {r.document_file_url && (
                                                <button
                                                    onClick={() => openRemoteFilePreview(r.document_file_url, 'Proof of Receivable')}
                                                    style={{
                                                        background: '#1a73e8', color: '#fff', border: 'none',
                                                        borderRadius: 4, padding: '4px 12px', cursor: 'pointer',
                                                        fontSize: '0.8rem', fontWeight: 600
                                                    }}
                                                >
                                                    Proof
                                                </button>
                                            )}
                                            {r.acknowledgement_file_url && (
                                                <button
                                                    onClick={() => openRemoteFilePreview(r.acknowledgement_file_url, 'Receipt')}
                                                    style={{
                                                        background: '#16a34a', color: '#fff', border: 'none',
                                                        borderRadius: 4, padding: '4px 12px', cursor: 'pointer',
                                                        fontSize: '0.8rem', fontWeight: 600
                                                    }}
                                                >
                                                    Receipt
                                                </button>
                                            )}
                                            {!r.acknowledgement_file_url && !r.document_file_url && '-'}
                                        </div>
                                    </td>
                                    <td style={{ ...tdStyle, textTransform: 'uppercase' }}>
                                        <div>{r.created_by_name || '-'}</div>
                                        <div style={{ fontSize: 10, color: '#888' }}>{formatDateTime(r.created_at)}</div>
                                    </td>
                                    <td style={tdStyle}>
                                        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                                            {canEdit && (
                                                <button
                                                    className="icon-btn"
                                                    style={{ color: 'blue' }}
                                                    onClick={() => openEditModal(r)}
                                                    title="Edit"
                                                >
                                                    <i className="fa fa-pen"></i>
                                                </button>
                                            )}
                                            {canDelete && (
                                                <button
                                                    className="icon-btn"
                                                    style={{ color: 'red' }}
                                                    onClick={() => setDeleteTarget(r)}
                                                    title="Delete"
                                                >
                                                    <i className="fa fa-trash"></i>
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Add Receivable Modal */}
            {showModal && (
                <div style={overlayStyle}>
                    <div style={modalStyle}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
                            <h3 style={{ margin: 0 }}>{editingReceivable ? 'Edit Receivable' : 'New Receivable'}</h3>
                            <button
                                onClick={() => { setShowModal(false); setEditingReceivable(null); }}
                                style={{ background: 'none', border: 'none', fontSize: '1.4rem', cursor: 'pointer', color: '#666' }}
                            >
                                &times;
                            </button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            {/* Row 1: Customer | Refund */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 12, alignItems: 'start' }}>
                                <div style={fieldGroup}>
                                    <label style={labelStyle}>CUSTOMER <span style={{ color: 'red' }}>*</span></label>
                                    <Select2Dropdown
                                        options={customers}
                                        value={formData.customer}
                                        onChange={val => handleFormChange('customer', val)}
                                        placeholder="Select Customer..."
                                        displayKey="text"
                                        valueKey="id"
                                    />
                                    {formErrors.customer && <div style={errStyle}>{formErrors.customer}</div>}
                                </div>
                                <div style={{ ...fieldGroup, paddingTop: 22, minWidth: 90 }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer', userSelect: 'none' }}>
                                        <input
                                            type="checkbox"
                                            checked={formData.is_refund}
                                            onChange={e => handleFormChange('is_refund', e.target.checked)}
                                            style={{ width: 16, height: 16, cursor: 'pointer', accentColor: '#e65100' }}
                                        />
                                        <span style={{ fontSize: '0.82rem', fontWeight: 700, color: '#e65100', letterSpacing: '0.04em' }}>REFUND</span>
                                    </label>
                                </div>
                            </div>

                            {/* Row 2: Mode | Date */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                                <div style={fieldGroup}>
                                    <label style={labelStyle}>MODE <span style={{ color: 'red' }}>*</span></label>
                                    <Select2Dropdown
                                        options={MODES}
                                        value={formData.mode}
                                        onChange={val => handleFormChange('mode', val)}
                                        placeholder="Select Mode..."
                                        displayKey="text"
                                        valueKey="id"
                                    />
                                    {formErrors.mode && <div style={errStyle}>{formErrors.mode}</div>}
                                </div>
                                <div style={fieldGroup}>
                                    <label style={labelStyle}>DATE <span style={{ color: 'red' }}>*</span></label>
                                    <input
                                        type="date"
                                        value={formData.date}
                                        onChange={e => handleFormChange('date', e.target.value)}
                                        style={inputStyle}
                                    />
                                    {formErrors.date && <div style={errStyle}>{formErrors.date}</div>}
                                </div>
                            </div>

                            {/* Row 3: Bank/Currency | Amount */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                                {isCash ? (
                                    <div style={fieldGroup}>
                                        <label style={labelStyle}>CURRENCY <span style={{ color: 'red' }}>*</span></label>
                                        <Select2Dropdown
                                            options={CASH_CURRENCIES}
                                            value={formData.currency}
                                            onChange={val => handleFormChange('currency', val)}
                                            placeholder="Select Currency..."
                                            displayKey="text"
                                            valueKey="id"
                                        />
                                        {formErrors.currency && <div style={errStyle}>{formErrors.currency}</div>}
                                    </div>
                                ) : (
                                    <div style={fieldGroup}>
                                        <label style={labelStyle}>BANK ACCOUNT <span style={{ color: 'red' }}>*</span></label>
                                        <Select2Dropdown
                                            options={bankOptions}
                                            value={formData.bank_account}
                                            onChange={val => handleFormChange('bank_account', val)}
                                            placeholder="Select Bank Account..."
                                            displayKey="text"
                                            valueKey="id"
                                        />
                                        {formErrors.bank_account && <div style={errStyle}>{formErrors.bank_account}</div>}
                                        {formData.bank_account && formData.currency && (
                                            <div style={{ marginTop: 4, fontSize: '0.82rem', color: '#5c35a0', fontWeight: 600 }}>
                                                Currency: {formData.currency}
                                            </div>
                                        )}
                                    </div>
                                )}
                                <div style={fieldGroup}>
                                    <label style={labelStyle}>AMOUNT ({formData.currency || 'TZS'}) <span style={{ color: 'red' }}>*</span></label>
                                    <input
                                        type="text"
                                        inputMode="decimal"
                                        value={formatAmountInput(formData.amount)}
                                        onChange={e => {
                                            const raw = e.target.value.replace(/[^0-9.]/g, '');
                                            const parts = raw.split('.');
                                            const cleaned = parts.length > 2
                                                ? `${parts[0]}.${parts.slice(1).join('')}`
                                                : raw;
                                            handleFormChange('amount', cleaned);
                                        }}
                                        placeholder="0.00"
                                        style={inputStyle}
                                    />
                                    {formErrors.amount && <div style={errStyle}>{formErrors.amount}</div>}
                                </div>
                            </div>

                            {/* Row 4: Reference No | Brought By (cash) | Remark */}
                            <div style={{ display: 'grid', gridTemplateColumns: isCash ? '1fr 1fr 1fr' : '1fr 1fr', gap: 12 }}>
                                <div style={fieldGroup}>
                                    <label style={labelStyle}>
                                        {isCash ? 'REFERENCE NO' : 'REF NO / CHEQUE NO / VOUCHER NO'}
                                        {!isCash && <span style={{ color: 'red' }}> *</span>}
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.reference_no}
                                        onChange={e => !isCash && handleFormChange('reference_no', e.target.value)}
                                        placeholder={isCash ? 'Auto-generated' : 'Enter reference number...'}
                                        readOnly={isCash}
                                        style={{ ...inputStyle, ...(isCash ? { background: '#f0f0f0', color: '#555', cursor: 'not-allowed' } : {}) }}
                                    />
                                    {formErrors.reference_no && <div style={errStyle}>{formErrors.reference_no}</div>}
                                </div>
                                {isCash && (
                                    <div style={fieldGroup}>
                                        <label style={labelStyle}>BROUGHT BY <span style={{ color: 'red' }}>*</span></label>
                                        <input
                                            type="text"
                                            value={formData.brought_by}
                                            onChange={e => handleFormChange('brought_by', e.target.value)}
                                            placeholder="Enter who brought the cash..."
                                            style={inputStyle}
                                        />
                                        {formErrors.brought_by && <div style={errStyle}>{formErrors.brought_by}</div>}
                                    </div>
                                )}
                                <div style={fieldGroup}>
                                    <label style={labelStyle}>REMARK</label>
                                    <input
                                        type="text"
                                        value={formData.remark}
                                        onChange={e => handleFormChange('remark', e.target.value)}
                                        placeholder="Enter remark..."
                                        style={inputStyle}
                                    />
                                    {formErrors.remark && <div style={errStyle}>{formErrors.remark}</div>}
                                </div>
                            </div>

                            {/* Document Upload */}
                            <div style={fieldGroup}>
                                <label style={labelStyle}>PROOF OF RECEIVABLE</label>
                                {editingReceivable && editingReceivable.document_file_url && !replaceDocument ? (
                                    <div style={{
                                        border: '1px solid #ced4da',
                                        borderRadius: 5, padding: '8px 12px', background: '#f8f9fa',
                                        display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap'
                                    }}>
                                        <span style={{ flex: 1, fontSize: '0.85rem', color: '#555', wordBreak: 'break-all' }}>
                                            {editingReceivable.document_file_url.split('/').pop()}
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() => openRemoteFilePreview(editingReceivable.document_file_url, 'Proof of Receivable')}
                                            style={{
                                                background: '#1a73e8', color: '#fff', border: 'none', borderRadius: 4,
                                                padding: '4px 12px', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer'
                                            }}
                                        >
                                            View
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => { setReplaceDocument(true); handleFormChange('document_file', null); }}
                                            style={{
                                                background: '#6c757d', color: '#fff', border: 'none', borderRadius: 4,
                                                padding: '4px 12px', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer'
                                            }}
                                        >
                                            Replace
                                        </button>
                                    </div>
                                ) : (
                                    <div style={{
                                        border: `1px solid ${formErrors.document_file ? '#dc3545' : '#ced4da'}`,
                                        borderRadius: 5, padding: '8px 12px', background: '#f8f9fa'
                                    }}>
                                        <input
                                            type="file"
                                            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                                            onChange={e => handleFormChange('document_file', e.target.files[0] || null)}
                                            style={{ fontSize: '0.88rem' }}
                                        />
                                        {editingReceivable && editingReceivable.document_file_url && (
                                            <button
                                                type="button"
                                                onClick={() => { setReplaceDocument(false); handleFormChange('document_file', null); }}
                                                style={{
                                                    marginTop: 8, background: 'transparent', color: '#1a73e8', border: 'none',
                                                    padding: 0, fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', textDecoration: 'underline'
                                                }}
                                            >
                                                Cancel replacement
                                            </button>
                                        )}
                                        <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>
                                            Document Type: <strong>PROOF OF RECEIVABLE</strong> &nbsp;·&nbsp; PDF, Image, or Word document (Optional)
                                        </div>
                                    </div>
                                )}
                                {formErrors.document_file && <div style={errStyle}>{formErrors.document_file}</div>}
                            </div>

                            {/* Actions */}
                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    style={{
                                        background: '#1a73e8', color: '#fff', border: 'none',
                                        borderRadius: 6, padding: '8px 20px', fontWeight: 600,
                                        cursor: submitting ? 'not-allowed' : 'pointer', opacity: submitting ? 0.7 : 1
                                    }}
                                >
                                    {submitting ? 'Saving...' : 'Save Receivable'}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setShowModal(false)}
                                    disabled={submitting}
                                    style={{
                                        background: '#6c757d', color: '#fff', border: 'none',
                                        borderRadius: 6, padding: '8px 20px', fontWeight: 600, cursor: 'pointer'
                                    }}
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Delete Confirm Dialog */}
            {deleteTarget && (
                <div style={overlayStyle}>
                    <div style={{ ...modalStyle, maxWidth: 400 }}>
                        <h3 style={{ marginTop: 0 }}>Confirm Delete</h3>
                        <p>
                            Are you sure you want to delete the receivable of{' '}
                            <strong>TZS {formatCurrency(deleteTarget.amount)}</strong> from{' '}
                            <strong>{deleteTarget.customer_name}</strong> dated{' '}
                            <strong>{formatDateDisplay(deleteTarget.date)}</strong>?
                        </p>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                            <button
                                onClick={() => setDeleteTarget(null)}
                                disabled={deleting}
                                style={{
                                    background: '#6c757d', color: '#fff', border: 'none',
                                    borderRadius: 6, padding: '7px 18px', fontWeight: 600, cursor: 'pointer'
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDelete}
                                disabled={deleting}
                                style={{
                                    background: '#dc3545', color: '#fff', border: 'none',
                                    borderRadius: 6, padding: '7px 18px', fontWeight: 600,
                                    cursor: deleting ? 'not-allowed' : 'pointer', opacity: deleting ? 0.7 : 1
                                }}
                            >
                                {deleting ? 'Deleting...' : 'Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// Helper styles
const thStyle = {
    padding: '10px 12px', textAlign: 'left', fontWeight: 600,
    whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none', fontSize: '0.85rem'
};
const tdStyle = { padding: '8px 12px', verticalAlign: 'middle' };
const overlayStyle = {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.45)', zIndex: 1000,
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16
};
const modalStyle = {
    background: '#fff', borderRadius: 10, padding: '24px 28px',
    width: '100%', maxWidth: 560, maxHeight: '90vh', overflowY: 'auto',
    boxShadow: '0 8px 32px rgba(0,0,0,0.22)'
};
const fieldGroup = { marginBottom: 14 };
const labelStyle = { display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#555', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.03em' };
const inputStyle = {
    width: '100%', padding: '7px 10px', borderRadius: 5,
    border: '1px solid #ced4da', fontSize: '0.9rem', boxSizing: 'border-box',
    textTransform: 'uppercase'
};
const errStyle = { color: '#dc3545', fontSize: '0.78rem', marginTop: 3 };

const paymentModeColor = (mode) => {
    switch (mode) {
        case 'CASH': return { bg: '#e8f5e9', color: '#2e7d32' };
        case 'CHEQUE': return { bg: '#e3f2fd', color: '#1565c0' };
        case 'BANK TRANSFER': return { bg: '#f3e5f5', color: '#6a1b9a' };
        case 'BANK DEPOSIT': return { bg: '#fff3e0', color: '#e65100' };
        default: return { bg: '#f5f5f5', color: '#333' };
    }
};

export default Receivables;
